// createHttpClient — the TS port of the v3 `createAxiosInstance` factory (contract 03 §3),
// stripped of its feature coupling. Interceptors implement, once and testably:
//   • bearer token injection (from the injected getToken)
//   • protected-call 401 → injected onUnauthorized (logout); 403/5xx/402 are NOT toasted.
//     `suppressGlobalErrorHandler` silences the toast only: every api/ class sets it, so letting
//     it also swallow the 401 meant an expired session never logged out. A caller that owns its
//     401 says so with `meta.ownsUnauthorized`.
//   • the backend's display policy: `error.user_visible:false` is not toasted (still rejects)
//   • cancellation as the first error branch (never toasted, never a logout trigger)
//   • error normalization to ApiError (no raw AxiosError escapes)
//   • an AbortSignal-based cancellation seam registered with the session (registerAbort)
//   • `suppressGlobalErrorHandler` — the request stays feature-owned, still rejects ApiError
//
// Retry/backoff is wired through `./retry` (ORP-18582): a failed GET is replayed before the
// error reaches the toast, so a flaky hop never becomes a user-visible failure. Perf logging
// IS wired (ORP-18581): the interceptor stamps request start and logs duration, payload size and
// a User Timing span on settle through `perfLogger` — behind that module's RUNTIME switch
// (ORP-22839; off by default, so this stays one cached boolean read per settled request).
import axios from 'axios'
import type {
  AxiosInstance,
  AxiosResponse,
  GenericAbortSignal,
  InternalAxiosRequestConfig,
} from 'axios'

import { ApiError, type ApiErrorKind } from './ApiError'
import { getHttpDeps } from './configure'
import { planRetry } from './retry'
import type { HttpClientOptions } from './types'

import { perfLogger } from '@/shared/lib/perf/perfLogger'
import { markHttpSpan } from '@/shared/lib/perf/perfMarks'

const DEFAULT_TIMEOUT_MS = 30_000

/** Errors the global toast must skip. 401 goes to onUnauthorized; canceled rejects silently. */
const SKIP_TOAST_KINDS = new Set<ApiErrorKind>([
  'unauthorized',
  'forbidden',
  'paymentRequired',
  'server',
  'canceled',
])

/** Per-request settle cleanup (detach linked signal listeners + session unregister), keyed by config. */
const abortCleanup = new WeakMap<object, () => void>()

/**
 * Per-request perf stamp (interceptor start time, keyed by config). Kept off `config.meta` so
 * this leaf need not touch the shared RequestMeta type; the log is emitted on settle through
 * `perfLogger`. Canceled requests are not logged — the perf log assumes a response and sits
 * behind the cancellation early-return (v3 parity).
 */
const perfStart = new WeakMap<object, number>()

export function createHttpClient(options: HttpClientOptions): AxiosInstance {
  const baseURL = options.baseURL
  const resolveBaseUrl = typeof baseURL === 'function' ? baseURL : (): string => baseURL

  const instance = axios.create({ timeout: options.timeoutMs ?? DEFAULT_TIMEOUT_MS })

  instance.interceptors.request.use((config) => onRequest(config, resolveBaseUrl, instance))
  instance.interceptors.response.use(
    (response) => onResponse(response),
    (error: unknown) => onResponseError(error, options, instance),
  )
  return instance
}

/** Distinct, truthy abort signals (axios discards `defaults.signal` when a per-call one exists). */
function uniqueSignals(signals: Array<GenericAbortSignal | undefined>): GenericAbortSignal[] {
  const out: GenericAbortSignal[] = []
  for (const signal of signals) if (signal && !out.includes(signal)) out.push(signal)
  return out
}

function onRequest(
  config: InternalAxiosRequestConfig,
  resolveBaseUrl: () => string,
  instance: AxiosInstance,
): InternalAxiosRequestConfig {
  const deps = getHttpDeps()

  if (!config.baseURL) config.baseURL = resolveBaseUrl()

  const token = deps?.getToken() ?? null
  if (token) config.headers.set('Authorization', `Bearer ${token}`)

  const timeoutMs = config.meta?.timeoutMs
  if (typeof timeoutMs === 'number') config.timeout = timeoutMs

  // Cancellation seam: feed EVERY source into one fresh controller so navigation
  // (`controllerHttp.defaults.signal`, module 05), component-unmount (a per-call signal) and
  // session teardown/logout (the injected registerAbort) all abort this in-flight request.
  // axios drops `defaults.signal` from the merged config when a per-call signal is present
  // (§11 requires them to compose), so we read the instance default explicitly and dedupe.
  if (deps?.registerAbort) {
    const controller = new AbortController()
    const sources = uniqueSignals([config.signal, instance.defaults.signal])
    const detachers: Array<() => void> = []
    for (const source of sources) {
      if (source.aborted) {
        controller.abort()
      } else {
        const onAbort = (): void => controller.abort()
        source.addEventListener?.('abort', onAbort, { once: true })
        detachers.push(() => source.removeEventListener?.('abort', onAbort))
      }
    }
    config.signal = controller.signal
    const unregister = deps.registerAbort(() => controller.abort())
    abortCleanup.set(config, () => {
      for (const detach of detachers) detach()
      unregister()
    })
  }

  perfStart.set(config, performance.now())

  return config
}

/**
 * Response payload size, best-effort (ORP-22839). `content-length` first — it is the wire size and
 * costs nothing. Falling back to serializing the decoded body is NOT free on the controller's
 * analytics payloads, so it only runs when the perf switch is on (the sole caller already checked).
 */
function responseBytes(response: AxiosResponse | undefined): number | undefined {
  if (!response) return undefined
  const headers = response.headers as { get?: (name: string) => unknown } & Record<string, unknown>
  const raw = headers?.get?.('content-length') ?? headers?.['content-length']
  const declared = Number(raw)
  if (Number.isFinite(declared) && declared > 0) return declared
  try {
    // Decoded size — usually larger than the gzipped wire size, and the only number available
    // cross-origin unless the server sends `Access-Control-Expose-Headers: content-length`.
    return JSON.stringify(response.data)?.length
  } catch {
    return undefined
  }
}

/** Emit one perf log + one User Timing span for a settled request. No-op while the switch is off. */
function logPerf(
  config: InternalAxiosRequestConfig | undefined,
  status: number | null,
  response?: AxiosResponse,
): void {
  if (!config) return
  const start = perfStart.get(config)
  if (start === undefined) return
  perfStart.delete(config)

  const end = performance.now()
  const method = config.method ?? 'get'
  const url = config.url ?? ''
  // Both of these self-gate on the switch; only the byte measurement is worth guarding here,
  // because its `content-length` fallback serializes the whole decoded body.
  markHttpSpan(method, url, start, end)
  perfLogger.logRequest({
    method,
    url,
    duration: Math.round(end - start),
    status,
    timestamp: Date.now(),
    bytes: perfLogger.isEnabled() ? responseBytes(response) : undefined,
  })
}

function onResponse(response: AxiosResponse): AxiosResponse {
  settle(response.config)
  logPerf(response.config, response.status, response)
  return response
}

function onResponseError(
  error: unknown,
  options: HttpClientOptions,
  instance: AxiosInstance,
): Promise<AxiosResponse | never> {
  const config = configOf(error)
  settle(config)

  const apiError = ApiError.fromAxios(error)
  // Cancellation is the first branch (v3 parity): skip the perf log that assumes a response.
  if (apiError.kind === 'canceled') return Promise.reject(apiError)

  logPerf(config, apiError.status)

  // Before any side effect: a replay that succeeds must leave no toast and no logout behind.
  // Each attempt has already settled and logged its own perf above, so the replay is a clean
  // trip through the whole chain.
  const retryIn = planRetry(apiError, config)
  if (retryIn !== null && config) {
    return sleep(retryIn).then(() => instance.request(config))
  }

  const deps = getHttpDeps()
  if (apiError.kind === 'unauthorized') {
    // A protected request can fail with a missing/invalid header as well as an expired bearer.
    // Both responses invalidate the current UI session; public auth flows opt out explicitly via
    // `meta.ownsUnauthorized` (e.g. wrong login credentials).
    if (!config?.meta?.ownsUnauthorized) {
      const onUnauthorized = options.onUnauthorized ?? deps?.onUnauthorized
      onUnauthorized?.(apiError)
    }
  } else if (
    !isSuppressed(config) &&
    apiError.userVisible &&
    !SKIP_TOAST_KINDS.has(apiError.kind)
  ) {
    const onError = options.onError ?? deps?.onError
    onError?.(apiError)
  }

  return Promise.reject(apiError)
}

/** Detach the request's linked signal listeners + session unregister, then fire onSettle (all crash-safe). */
function settle(config: object | undefined): void {
  if (config) {
    const cleanup = abortCleanup.get(config)
    if (cleanup) {
      abortCleanup.delete(config)
      try {
        cleanup()
      } catch {
        /* teardown must not throw */
      }
    }
  }
  try {
    getHttpDeps()?.onSettle?.()
  } catch {
    /* settle hook must not throw */
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function configOf(error: unknown): InternalAxiosRequestConfig | undefined {
  if (error && typeof error === 'object' && 'config' in error) {
    return (error as { config?: InternalAxiosRequestConfig }).config
  }
  return undefined
}

function isSuppressed(config: InternalAxiosRequestConfig | undefined): boolean {
  return Boolean(config?.suppressGlobalErrorHandler || config?.meta?.suppressGlobalErrorHandler)
}
